import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  get,
  post,
  put,
  setAccessToken,
  refreshAccessToken,
  registerRefreshedUserCallback,
  registerUnauthorizedCallback,
} from '../api/client';
import { queryClient } from '../lib/queryClient';
import { resetRejectedOrdersNoticePopup } from '../api/orderApi';
import { useMembershipStore } from './membershipStore';

/** 将未知异常归一化为带 code/data 的 Error，保持各登录入口的错误处理一致 */
function toAuthError(error: unknown, fallback: string): Error & { code?: string; data?: unknown } {
  const source = (error ?? {}) as { message?: unknown; code?: unknown; data?: unknown };
  return Object.assign(new Error(typeof source.message === 'string' && source.message ? source.message : fallback), {
    code: typeof source.code === 'string' ? source.code : undefined,
    data: source.data,
  });
}

export interface PartyProfile {
  id: string;
  fullName: string | null;
  username: string | null;
  idCard: string | null;
  avatar: string | null;
  role: string | null;
  /**
   * 后端算好的管理员标记（role 为 'admin' 或历史值 '管理员' 时都为 true）。
   * 鉴权判断一律读它，不要再自己比对 role 字符串。
   */
  isAdmin: boolean;
  email: string | null;
  phone: string | null;
  backupPhone: string | null;
  companyName: string | null;
  location: string | null;
  address: string | null;
  introduction: string | null;
  emergencyContact: string | null;
  emergencyPhone: string | null;
  createdAt: string | null;
  userLevel: string | null;
  userLevelExpireAt: string | null;
}

export interface User {
  id: string;
  email: string | null;
  phone: string | null;
  username: string | null;
  role: string | null;
  /** 后端算好的管理员标记；旧版本持久化到 localStorage 的会话里没有该字段，故为可选 */
  isAdmin?: boolean;
  status: string | null;
  createdAt: string | null;
}

/**
 * 前端会话。只含 accessToken——refreshToken 归 HttpOnly Cookie，
 * 前端既读不到也不落地，故不设对应字段以免诱导后人往里存真值。
 */
export interface Session {
  accessToken: string;
  user: User;
  expiresAt: number;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: PartyProfile | null;
  loading: boolean;
  _hydrate: () => void;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithIdentifier: (
    identifier: string,
    password: string,
    type: 'email' | 'username' | 'phone'
  ) => Promise<{ error: Error | null }>;
  /**
   * 注册即登录：注册成功后用后端签发的令牌建立会话（免激活）。
   * 只接 accessToken——refreshToken 走 HttpOnly Cookie，不在响应体里。
   */
  applyTokenPair: (
    tokens: { accessToken: string },
    userData: User
  ) => Promise<void>;
  forgotPassword: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (
    updates: Partial<Omit<PartyProfile, 'id' | 'role' | 'createdAt'>>
  ) => Promise<{ error: Error | null }>;
  resetStore: () => void;
}

async function fetchProfileById(userId: string): Promise<PartyProfile | null> {
  try {
    return await get<PartyProfile>(`/users/${userId}/profile`);
  } catch (error) {
    console.error('[AuthStore] fetchProfile error:', error);
    return null;
  }
}

/**
 * 统一的「会话结束」清空动作：登出、刷新失败、水合失败、手动重置都走这一条路。
 *
 * 必须连带清空会员 store。否则同一浏览器上换账号登录时，membershipStore 里还留着
 * 上一个账号的 `status` 与 `loadedFor`，新账号在 GET /membership/status 返回之前
 * 会短暂沿用旧账号的等级与功能权限（usePermission 直接读该 store）。
 *
 * 同理必须清空 react-query 缓存：订单、客户、对账、通知等查询结果都在内存里，
 * 换账号后新账号首屏会先渲染上一个账号的数据（含姓名、电话、地址等 PII），
 * 直到各自请求返回才被覆盖。
 */
function clearSession() {
  queryClient.clear();
  useMembershipStore.getState().reset();
  // 「被驳回订单」登录弹窗的复位点放这里，而不是放在某个登录成功回调里：
  // 账号密码、验证码、扫码三条入口都会先经过会话结束再到登录，一处复位全覆盖。
  resetRejectedOrdersNoticePopup();
  useAuthStore.setState({ user: null, session: null, profile: null, loading: false });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      profile: null,
      loading: true,

      // 初始化：启动时静默调 /auth/refresh，若 HttpOnly Cookie 里的
      // refreshToken 仍有效则恢复登录态
      _hydrate: async () => {
        try {
          const token = await refreshAccessToken();
          if (!token) {
            clearSession();
            return;
          }
          // refreshAccessToken 已通过回调设置 user，这里从 store 读确认
          const currentUser = get().user;
          if (currentUser) {
            const session: Session = {
              accessToken: token,
              user: currentUser,
              expiresAt: Date.now() + 15 * 60 * 1000, // accessToken 15 分钟
            };
            set({ session });
            fetchProfileById(currentUser.id)
              .then(profile => set({ profile, loading: false }))
              .catch(() => set({ loading: false }));
          } else {
            set({ loading: false });
          }
        } catch {
          clearSession();
        }
      },

      signIn: async (email: string, password: string) => {
        try {
          // 后端 /auth/login 按 identifier+type 解析（不直接读 email 字段），
          // 故以 email 作为 identifier、type='email' 提交，避免登录标识符为空导致必然失败。
          // refreshToken 由后端写入 HttpOnly Cookie，不出现在响应体里
          const data = await post<{ accessToken: string; user: User }>(
            '/auth/login',
            { identifier: email, type: 'email', password }
          );
          const { accessToken, user: userData } = data;
          
          const session: Session = {
            accessToken,
            user: userData,
            expiresAt: Date.now() + 15 * 60 * 1000 // 15 分钟
          };
          
          setAccessToken(accessToken);
          set({ session, user: userData, loading: false });
          // profile 可以后台异步加载
          fetchProfileById(userData.id).then(profile => set({ profile }));
          
          return { error: null };
        } catch (error) {
          console.error('[signIn] error:', error);
          return { error: toAuthError(error, '登录失败，请重试') };
        }
      },

      signInWithIdentifier: async (
        identifier: string,
        password: string,
        type: 'email' | 'username' | 'phone'
      ) => {
        try {
          const data = await post<{ accessToken: string; user: User }>(
            '/auth/login',
            { identifier, password, type }
          );
          
          const { accessToken, user: userData } = data;
          
          // 防御：userData 不应为空，若为空说明响应结构异常
          if (!userData || !userData.id) {
            // 不得把 data 挂到 Error 上，也不得 JSON.stringify(data)：
            // 里面是 accessToken，任何 console.error(error) 都会把令牌打进控制台
            console.error('[signInWithIdentifier] 响应缺少 user 字段');
            return { error: new Error('登录响应异常，请联系开发人员') };
          }
          
          const session: Session = {
            accessToken,
            user: userData,
            expiresAt: Date.now() + 15 * 60 * 1000 // 15 分钟
          };
          
          // accessToken 放内存；refreshToken 由后端写入 HttpOnly Cookie，前端不持有
          setAccessToken(accessToken);
          set({ session, user: userData, loading: false });
          // profile 后台异步加载
          fetchProfileById(userData.id).then(profile => set({ profile }));
          
          return { error: null };
        } catch (error) {
          console.error('[signInWithIdentifier] error:', error);
          return { error: toAuthError(error, '登录失败，请重试') };
        }
      },

      forgotPassword: async (email: string) => {
        try {
          await post('/auth/forgot-password', { email });
          return { error: null };
        } catch (error) {
          console.error('[forgotPassword] error:', error);
          return { error: toAuthError(error, '发送重置邮件失败，请重试') };
        }
      },

      applyTokenPair: async (tokens, userData) => {
        if (!userData || !userData.id) {
          console.error('[applyTokenPair] 用户数据缺失');
          return;
        }
        // accessToken 放内存；refreshToken 由注册端点经 deliverRefreshToken
        // 写入 HttpOnly Cookie（与登录端点同一通道），已从响应体删除，前端不持有
        setAccessToken(tokens.accessToken);
        const session: Session = {
          accessToken: tokens.accessToken,
          user: userData,
          expiresAt: Date.now() + 15 * 60 * 1000,
        };
        set({ session, user: userData, loading: false });
        fetchProfileById(userData.id).then(profile => set({ profile }));
      },

      signOut: async () => {
        try {
          await post('/auth/logout');
        } catch (error) {
          console.error('[signOut] error:', error);
        } finally {
          // refreshToken 的 HttpOnly Cookie 由后端 POST /auth/logout 清除，
          // 前端只需丢掉内存里的 accessToken 与本 store 的会话状态
          setAccessToken(null);
          clearSession();
          // 清除旧版 localStorage 残留（如有）
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
        }
      },

      refreshProfile: async () => {
        const { user } = get();
        if (!user) return;
        const profile = await fetchProfileById(user.id);
        set({ profile });
      },

      updateProfile: async (
        updates: Partial<Omit<PartyProfile, 'id' | 'role' | 'createdAt'>>
      ) => {
        const { user } = get();
        if (!user) return { error: new Error('未登录') };
        try {
          await put(`/users/${user.id}/profile`, updates);
          const profile = await fetchProfileById(user.id);
          set({ profile });
          return { error: null };
        } catch (error) {
          console.error('[updateProfile] error:', error);
          return { error: error as Error };
        }
      },

      resetStore: () => {
        clearSession();
      }
    }),
    {
      name: 'auth-storage',
      version: 1,
      // 仅持久化 user 基本信息用于刷新后 UI 快速显示；session.accessToken 一律放内存。
      // profile 含身份证号/手机/地址/紧急联系人等敏感 PII，禁止落 localStorage（XSS 可读），
      // 改由 _hydrate 在 /auth/refresh 成功后经 fetchProfileById 运行时拉取（该窗口由 loading 兜底）。
      // 真实认证以 HttpOnly refreshToken cookie 为准，刷新页面时通过 _hydrate 重建会话。
      partialize: (state) => ({
        user: state.user,
      }),
      // 迁移：清除历史版本(v0)遗留在 localStorage 的 profile（含身份证等明文 PII），
      // 升级到 v1 后立即抹除旧敏感数据，而非等待下一次写入才被覆盖。
      migrate: (persisted) => {
        const state = (persisted ?? {}) as { user?: User | null; profile?: unknown };
        return { user: state.user ?? null };
      },
    }
  )
);

// 注册 client.ts 的回调（避免循环依赖）
// 1) refresh 成功后，同步最新 user 到 store
registerRefreshedUserCallback((user) => {
  useAuthStore.setState({ user });
});

// 2) refresh 失败（token 无效且无法刷新），清空认证状态
registerUnauthorizedCallback(() => {
  clearSession();
});
